/**
 * @fileoverview Sudoku Game Play Screen
 * @description Active Sudoku gameplay with board, controls, and timer
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Alert,
  ActivityIndicator,
  BackHandler,
  Modal,
  Pressable,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { router, useLocalSearchParams } from "expo-router";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  SudokuGameState,
  SudokuResult,
  DIFFICULTY_CONFIGS,
  SudokuWeekendConfig,
  DEFAULT_WEEKEND_CONFIG,
} from "@/modules/sudoku";
import { SudokuBoard } from "@/modules/sudoku/components/SudokuBoard";
import { NumberPad } from "@/modules/sudoku/components/NumberPad";
import { GameControls } from "@/modules/sudoku/components/GameControls";
import { SudokuResultOverlay } from "@/modules/sudoku/components/SudokuResultOverlay";
import {
  isCorrectValue,
  isPuzzleComplete,
  getHintCell,
  getCompletedNumbers,
} from "@/modules/sudoku/utils/sudokuValidator";

/**
 * Sudoku Game Screen
 */
export default function SudokuGameScreen() {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, userTeam, activeWeekend } = useAuth();
  const { sessionId } = useLocalSearchParams<{ sessionId: string }>();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState<{
    id: string;
    mode: string;
    game_state: SudokuGameState;
  } | null>(null);
  const [gameState, setGameState] = useState<SudokuGameState | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [result, setResult] = useState<SudokuResult | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [weekendConfig, setWeekendConfig] = useState<SudokuWeekendConfig>(
    DEFAULT_WEEKEND_CONFIG
  );
  const [lottieConfig, setLottieConfig] = useState<{
    win_lottie?: string;
    lose_lottie?: string;
    perfect_lottie?: string;
  }>({});

  // Refs
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSaveRef = useRef<number>(0);
  const gameStateRef = useRef<SudokuGameState | null>(null);

  const isGameActive = gameState && !gameState.is_completed && !gameState.is_failed && !isPaused;
  const config = gameState ? DIFFICULTY_CONFIGS[gameState.difficulty] : DIFFICULTY_CONFIGS.easy;

  /**
   * Load session data
   */
  const loadSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      const { data, error } = await supabase
        .from("game_sessions")
        .select("id, mode, game_state, weekend_id")
        .eq("id", sessionId)
        .single();

      if (error) throw error;

      // Ensure notes fields have defaults (for sessions created before notes were added)
      const loadedState: SudokuGameState = {
        ...(data.game_state as SudokuGameState),
        notes: (data.game_state as SudokuGameState).notes ?? {},
        is_notes_mode: (data.game_state as SudokuGameState).is_notes_mode ?? false,
      };

      setSessionData({
        id: data.id,
        mode: data.mode,
        game_state: loadedState,
      });
      setGameState(loadedState);
      gameStateRef.current = loadedState;
      setTimerSeconds(loadedState.time_elapsed_seconds || 0);

      // Load game definition config for lottie settings
      const { data: gameDefData } = await supabase
        .from("game_definitions")
        .select("default_config")
        .eq("slug", "sudoku")
        .single();

      if (gameDefData?.default_config) {
        const defConfig = gameDefData.default_config as Record<string, unknown>;
        setLottieConfig({
          win_lottie: defConfig.win_lottie as string | undefined,
          lose_lottie: defConfig.lose_lottie as string | undefined,
          perfect_lottie: defConfig.perfect_lottie as string | undefined,
        });
      }

      // Load weekend config if weekend mode
      if (data.mode === "weekend" && data.weekend_id) {
        const { data: configData } = await supabase
          .from("weekend_game_config")
          .select("points_config, custom_config")
          .eq("weekend_id", data.weekend_id)
          .eq("game_slug", "sudoku")
          .single();

        if (configData) {
          const pointsConfig = configData.points_config as Record<string, number> | null;
          const customConfig = configData.custom_config as Partial<SudokuWeekendConfig> | null;

          setWeekendConfig({
            ...DEFAULT_WEEKEND_CONFIG,
            ...customConfig,
            points_per_difficulty: {
              easy: pointsConfig?.easy ?? DEFAULT_WEEKEND_CONFIG.points_per_difficulty.easy,
              medium: pointsConfig?.medium ?? DEFAULT_WEEKEND_CONFIG.points_per_difficulty.medium,
              hard: pointsConfig?.hard ?? DEFAULT_WEEKEND_CONFIG.points_per_difficulty.hard,
            },
            perfect_bonus: pointsConfig?.perfect_bonus ?? DEFAULT_WEEKEND_CONFIG.perfect_bonus,
          });
        }
      }
    } catch (err) {
      console.error("[Sudoku] Error loading session:", err);
      Alert.alert("Fout", "Kon spel niet laden", [
        { text: "OK", onPress: () => router.back() },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Keep ref in sync with latest game state
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  /**
   * Timer management
   */
  useEffect(() => {
    if (isGameActive && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    } else if (!isGameActive && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isGameActive]);

  /**
   * Auto-save game state periodically
   */
  useEffect(() => {
    const saveInterval = setInterval(() => {
      if (gameState && sessionId && Date.now() - lastSaveRef.current > 5000) {
        saveGameState(gameState);
      }
    }, 10000);

    return () => clearInterval(saveInterval);
  }, [gameState, sessionId]);

  /**
   * Handle back button press
   */
  useEffect(() => {
    const backHandler = BackHandler.addEventListener("hardwareBackPress", () => {
      handleBack();
      return true;
    });

    return () => backHandler.remove();
  }, [isPaused]);

  /**
   * Save game state to database
   */
  const saveGameState = async (state: SudokuGameState) => {
    if (!sessionId) return;

    const stateWithTime = {
      ...state,
      time_elapsed_seconds: timerSeconds,
    };

    try {
      await supabase
        .from("game_sessions")
        .update({ game_state: stateWithTime })
        .eq("id", sessionId);

      lastSaveRef.current = Date.now();
    } catch (err) {
      console.error("[Sudoku] Error saving game state:", err);
    }
  };

  /**
   * Complete the game
   */
  const completeGame = async (
    state: SudokuGameState,
    isWin: boolean,
    isPerfect: boolean
  ) => {
    if (!sessionId || !activeProfile) return;

    // Calculate points
    let points = 0;
    if (isWin && sessionData?.mode === "weekend") {
      points = weekendConfig.points_per_difficulty[state.difficulty];
      if (isPerfect) {
        points += weekendConfig.perfect_bonus;
      }
    }

    const gameResult: SudokuResult = {
      completed: isWin,
      failed: !isWin,
      time_seconds: timerSeconds,
      mistakes: state.mistakes,
      hints_used: state.hints_used,
      is_perfect: isPerfect,
      difficulty: state.difficulty,
      points_earned: points,
    };

    setResult(gameResult);

    // Update session
    try {
      await supabase
        .from("game_sessions")
        .update({
          status: "completed",
          game_state: {
            ...state,
            is_completed: isWin,
            is_failed: !isWin,
            completed_at: new Date().toISOString(),
            time_elapsed_seconds: timerSeconds,
          },
          result: {
            is_win: isWin,
            winner_user_id: isWin ? activeProfile.id : null,
            winner_team_id: isWin ? userTeam?.id : null,
            final_score: timerSeconds,
            custom_data: {
              time_seconds: timerSeconds,
              mistakes: state.mistakes,
              hints_used: state.hints_used,
              is_perfect: isPerfect,
              difficulty: state.difficulty,
            },
          },
          points_awarded: points,
          completed_at: new Date().toISOString(),
        })
        .eq("id", sessionId);

      // Award team points if weekend mode
      if (sessionData?.mode === "weekend" && points > 0 && userTeam?.id) {
        // Get current team score
        const { data: team } = await supabase
          .from("teams")
          .select("total_score")
          .eq("id", userTeam.id)
          .single();

        const previousTotal = team?.total_score || 0;
        const newTotal = previousTotal + points;

        // Add to score history
        await supabase.from("score_history").insert({
          team_id: userTeam.id,
          points,
          previous_total: previousTotal,
          new_total: newTotal,
          reason: `Sudoku ${config.label} voltooid`,
          game_id: sessionId,
          game_type: "sudoku",
          awarded_by: activeProfile.id,
          awarded_to: activeProfile.id,
        });

        // Update team total
        await supabase
          .from("teams")
          .update({ total_score: newTotal })
          .eq("id", userTeam.id);

        // Send notification to team members
        const { data: teamMembers } = await supabase
          .from("team_members")
          .select("user_id")
          .eq("team_id", userTeam.id)
          .neq("user_id", activeProfile.id);

        if (teamMembers && teamMembers.length > 0) {
          const notifications = teamMembers.map((member) => ({
            user_id: member.user_id,
            type: "points_earned",
            title: "Punten verdiend! 🎉",
            body: `${activeProfile.full_name} heeft ${points} punten verdiend met Sudoku!`,
            sender_id: activeProfile.id,
            weekend_id: activeWeekend?.id,
            notification_type: "game_update",
            lottie_animation: isPerfect ? "trophy" : "confetti",
            priority: "normal",
          }));

          await supabase.from("notifications").insert(notifications);
        }
      }

      // Update user stats
      await updateUserStats(state, isWin, isPerfect);
    } catch (err) {
      console.error("[Sudoku] Error completing game:", err);
    }
  };

  /**
   * Update user game stats
   */
  const updateUserStats = async (
    state: SudokuGameState,
    isWin: boolean,
    isPerfect: boolean
  ) => {
    if (!activeProfile) return;

    const mode = sessionData?.mode || "free";

    try {
      // Get existing stats
      const { data: existing } = await supabase
        .from("user_game_stats")
        .select("*")
        .eq("user_id", activeProfile.id)
        .eq("game_slug", "sudoku")
        .eq("mode", mode)
        .single();

      const currentStats = (existing?.stats || {
        easy: { games_played: 0, games_won: 0, best_time_seconds: null, total_time_seconds: 0, avg_mistakes: 0, perfect_games: 0 },
        medium: { games_played: 0, games_won: 0, best_time_seconds: null, total_time_seconds: 0, avg_mistakes: 0, perfect_games: 0 },
        hard: { games_played: 0, games_won: 0, best_time_seconds: null, total_time_seconds: 0, avg_mistakes: 0, perfect_games: 0 },
        total_hints_used: 0,
        total_mistakes: 0,
      }) as any;

      // Update difficulty-specific stats
      const diffStats = currentStats[state.difficulty];
      diffStats.games_played += 1;
      if (isWin) {
        diffStats.games_won += 1;
        if (!diffStats.best_time_seconds || timerSeconds < diffStats.best_time_seconds) {
          diffStats.best_time_seconds = timerSeconds;
        }
      }
      diffStats.total_time_seconds += timerSeconds;
      if (isPerfect) {
        diffStats.perfect_games += 1;
      }
      // Update average mistakes
      const totalGames = diffStats.games_played;
      diffStats.avg_mistakes = ((diffStats.avg_mistakes * (totalGames - 1)) + state.mistakes) / totalGames;

      // Update totals
      currentStats.total_hints_used += state.hints_used;
      currentStats.total_mistakes += state.mistakes;

      // Calculate overall best time
      let overallBestTime: number | null = null;
      ["easy", "medium", "hard"].forEach((d) => {
        const bt = currentStats[d].best_time_seconds;
        if (bt !== null && (overallBestTime === null || bt < overallBestTime)) {
          overallBestTime = bt;
        }
      });

      // Calculate overall games
      let totalGamesPlayed = 0;
      let totalGamesWon = 0;
      let totalPoints = existing?.total_points ?? 0;
      ["easy", "medium", "hard"].forEach((d) => {
        totalGamesPlayed += currentStats[d].games_played;
        totalGamesWon += currentStats[d].games_won;
      });

      // Add points if weekend mode
      if (mode === "weekend") {
        let points = weekendConfig.points_per_difficulty[state.difficulty];
        if (isPerfect) points += weekendConfig.perfect_bonus;
        if (isWin) totalPoints += points;
      }

      if (existing) {
        await supabase
          .from("user_game_stats")
          .update({
            games_played: totalGamesPlayed,
            games_won: totalGamesWon,
            total_points: totalPoints,
            personal_best_time_ms: overallBestTime ? overallBestTime * 1000 : null,
            stats: currentStats,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existing.id);
      } else {
        await supabase.from("user_game_stats").insert({
          user_id: activeProfile.id,
          game_slug: "sudoku",
          mode,
          games_played: 1,
          games_won: isWin ? 1 : 0,
          total_points: mode === "weekend" && isWin ? weekendConfig.points_per_difficulty[state.difficulty] + (isPerfect ? weekendConfig.perfect_bonus : 0) : 0,
          personal_best_time_ms: isWin ? timerSeconds * 1000 : null,
          stats: currentStats,
        });
      }
    } catch (err) {
      console.error("[Sudoku] Error updating user stats:", err);
    }
  };

  /**
   * Check for game over conditions
   */
  const checkGameOver = async (state: SudokuGameState) => {
    // Check if failed
    if (state.mistakes >= config.max_mistakes) {
      const failedState: SudokuGameState = {
        ...state,
        is_failed: true,
        completed_at: new Date().toISOString(),
      };
      setGameState(failedState);
      await completeGame(failedState, false, false);
      return true;
    }

    // Check if completed
    if (isPuzzleComplete(state.current, state.solution)) {
      const isPerfect = state.mistakes === 0 && state.hints_used === 0;
      const completedState: SudokuGameState = {
        ...state,
        is_completed: true,
        completed_at: new Date().toISOString(),
      };
      setGameState(completedState);
      await completeGame(completedState, true, isPerfect);
      return true;
    }

    return false;
  };

  /**
   * Handle cell selection
   */
  const handleCellPress = (index: number) => {
    const state = gameStateRef.current;
    if (!state || state.is_completed || state.is_failed) return;

    const newState = { ...state, selected_cell: index };
    gameStateRef.current = newState;
    setGameState(newState);
  };

  /**
   * Handle number input
   * Uses ref to always read the latest game state (avoids stale closures)
   */
  const handleNumberPress = async (num: number) => {
    const state = gameStateRef.current;
    if (!state || state.is_completed || state.is_failed) return;
    if (state.selected_cell === null) return;

    const cellIndex = state.selected_cell;
    const isGiven = state.puzzle[cellIndex] !== "0";
    if (isGiven) return;

    // Ensure notes object exists (defensive)
    const notes = state.notes ?? {};

    // Notes mode
    if (state.is_notes_mode) {
      // Can't add notes to a cell that already has a value — erase it first
      const currentValue = state.current[cellIndex];
      let baseCurrent = state.current;
      if (currentValue !== "0") {
        const arr = baseCurrent.split("");
        arr[cellIndex] = "0";
        baseCurrent = arr.join("");
      }

      const currentNotes = notes[cellIndex] || [];
      const newNotes = currentNotes.includes(num)
        ? currentNotes.filter((n) => n !== num)
        : [...currentNotes, num].sort();

      const newState: SudokuGameState = {
        ...state,
        current: baseCurrent,
        notes: {
          ...notes,
          [cellIndex]: newNotes,
        },
        moves: [
          ...state.moves,
          {
            cell_index: cellIndex,
            previous_value: currentValue === "0" ? null : (parseInt(currentValue, 10) as any),
            new_value: null,
            previous_notes: currentNotes,
            new_notes: newNotes,
            is_note_move: true,
            timestamp: new Date().toISOString(),
          },
        ],
      };

      gameStateRef.current = newState;
      setGameState(newState);
      await saveGameState(newState);
      return;
    }

    // Regular number entry
    const currentValue = state.current[cellIndex];
    const newValue = num.toString();

    if (currentValue === newValue) return;

    const currentArray = state.current.split("");
    currentArray[cellIndex] = newValue;
    const newCurrent = currentArray.join("");

    let newMistakes = state.mistakes;
    if (!isCorrectValue(state.solution, cellIndex, num)) {
      newMistakes++;
    }

    const newNotes = { ...notes };
    delete newNotes[cellIndex];

    const newState: SudokuGameState = {
      ...state,
      current: newCurrent,
      notes: newNotes,
      moves: [
        ...state.moves,
        {
          cell_index: cellIndex,
          previous_value: currentValue === "0" ? null : parseInt(currentValue, 10) as any,
          new_value: num as any,
          previous_notes: notes[cellIndex] || [],
          new_notes: [],
          is_note_move: false,
          timestamp: new Date().toISOString(),
        },
      ],
      mistakes: newMistakes,
    };

    gameStateRef.current = newState;
    setGameState(newState);
    await saveGameState(newState);
    await checkGameOver(newState);
  };

  /**
   * Handle clear button
   */
  const handleClear = async () => {
    const state = gameStateRef.current;
    if (!state || state.is_completed || state.is_failed) return;
    if (state.selected_cell === null) return;

    const cellIndex = state.selected_cell;
    const isGiven = state.puzzle[cellIndex] !== "0";
    if (isGiven) return;

    const currentValue = state.current[cellIndex];
    const notes = state.notes ?? {};

    // Nothing to clear
    if (currentValue === "0" && !(notes[cellIndex]?.length)) return;

    const currentArray = state.current.split("");
    currentArray[cellIndex] = "0";
    const newCurrent = currentArray.join("");

    const newNotes = { ...notes };
    delete newNotes[cellIndex];

    const newState: SudokuGameState = {
      ...state,
      current: newCurrent,
      notes: newNotes,
      moves: [
        ...state.moves,
        {
          cell_index: cellIndex,
          previous_value: currentValue === "0" ? null : (parseInt(currentValue, 10) as any),
          new_value: null,
          previous_notes: notes[cellIndex] || [],
          new_notes: [],
          is_note_move: false,
          timestamp: new Date().toISOString(),
        },
      ],
    };

    gameStateRef.current = newState;
    setGameState(newState);
    await saveGameState(newState);
  };

  /**
   * Handle undo
   */
  const handleUndo = async () => {
    const state = gameStateRef.current;
    if (!state || state.is_completed || state.is_failed) return;
    if (state.moves.length === 0) return;

    const lastMove = state.moves[state.moves.length - 1];
    const newMoves = state.moves.slice(0, -1);
    const notes = state.notes ?? {};

    let newCurrent = state.current;
    let newNotes = { ...notes };
    let newMistakes = state.mistakes;

    if (lastMove.is_note_move) {
      newNotes[lastMove.cell_index] = lastMove.previous_notes;
    } else {
      const currentArray = newCurrent.split("");
      currentArray[lastMove.cell_index] =
        lastMove.previous_value === null ? "0" : lastMove.previous_value.toString();
      newCurrent = currentArray.join("");

      if (lastMove.previous_notes.length > 0) {
        newNotes[lastMove.cell_index] = lastMove.previous_notes;
      }

      // Restore mistake count if was a mistake
      if (
        lastMove.new_value !== null &&
        !isCorrectValue(state.solution, lastMove.cell_index, lastMove.new_value)
      ) {
        newMistakes = Math.max(0, newMistakes - 1);
      }
    }

    const newState: SudokuGameState = {
      ...state,
      current: newCurrent,
      notes: newNotes,
      moves: newMoves,
      mistakes: newMistakes,
      selected_cell: lastMove.cell_index,
    };

    gameStateRef.current = newState;
    setGameState(newState);
    await saveGameState(newState);
  };

  /**
   * Handle hint
   */
  const handleHint = async () => {
    const state = gameStateRef.current;
    if (!state || state.is_completed || state.is_failed) return;

    const hintsRemaining = config.max_hints - state.hints_used;
    if (hintsRemaining <= 0) {
      Alert.alert("Geen hints meer", "Je hebt alle hints gebruikt.");
      return;
    }

    const hintIndex = getHintCell(
      state.current,
      state.solution,
      state.selected_cell
    );

    if (hintIndex === -1) {
      Alert.alert("Geen hint nodig", "De puzzel is al compleet!");
      return;
    }

    const notes = state.notes ?? {};
    const correctValue = state.solution[hintIndex];
    const currentArray = state.current.split("");
    currentArray[hintIndex] = correctValue;
    const newCurrent = currentArray.join("");

    const newNotes = { ...notes };
    delete newNotes[hintIndex];

    const newState: SudokuGameState = {
      ...state,
      current: newCurrent,
      notes: newNotes,
      hints_used: state.hints_used + 1,
      selected_cell: hintIndex,
    };

    gameStateRef.current = newState;
    setGameState(newState);
    await saveGameState(newState);
    await checkGameOver(newState);
  };

  /**
   * Toggle notes mode
   */
  const handleNotesToggle = () => {
    const state = gameStateRef.current;
    if (!state || state.is_completed || state.is_failed) return;

    const newState = { ...state, is_notes_mode: !state.is_notes_mode };
    gameStateRef.current = newState;
    setGameState(newState);
  };

  /**
   * Handle pause
   */
  const handlePause = () => {
    setIsPaused(true);
    setShowPauseModal(true);
    if (gameState) {
      saveGameState({ ...gameState, time_elapsed_seconds: timerSeconds });
    }
  };

  /**
   * Handle resume
   */
  const handleResume = () => {
    setIsPaused(false);
    setShowPauseModal(false);
  };

  /**
   * Handle back
   */
  const handleBack = () => {
    if (gameState && !gameState.is_completed && !gameState.is_failed) {
      setIsPaused(true);
      setShowPauseModal(true);
    } else {
      router.back();
    }
  };

  /**
   * Handle exit game
   */
  const handleExitGame = async () => {
    if (gameState) {
      await saveGameState({ ...gameState, time_elapsed_seconds: timerSeconds });
    }
    router.back();
  };

  /**
   * Handle play again
   */
  const handlePlayAgain = () => {
    setResult(null);
    router.replace("/(tabs)/games/sudoku" as any);
  };

  /**
   * Handle back to menu
   */
  const handleBackToMenu = () => {
    setResult(null);
    router.replace("/(tabs)/games/sudoku" as any);
  };

  if (isLoading || !gameState) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
            Laden...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const hintsRemaining = config.max_hints - gameState.hints_used;
  const completedNumbers = getCompletedNumbers(gameState.current);

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Game Controls Header */}
      <GameControls
        difficulty={gameState.difficulty}
        timerSeconds={timerSeconds}
        isPaused={isPaused}
        mistakes={gameState.mistakes}
        maxMistakes={config.max_mistakes}
        hintsUsed={gameState.hints_used}
        maxHints={config.max_hints}
        onPausePress={handlePause}
        onBackPress={handleBack}
      />

      {/* Game Board */}
      <View style={styles.boardContainer}>
        <SudokuBoard
          gameState={gameState}
          onCellPress={handleCellPress}
          disabled={isPaused || gameState.is_completed || gameState.is_failed}
        />
      </View>

      {/* Number Pad */}
      <NumberPad
        onNumberPress={handleNumberPress}
        onClearPress={handleClear}
        onUndoPress={handleUndo}
        onHintPress={handleHint}
        onNotesToggle={handleNotesToggle}
        isNotesMode={gameState.is_notes_mode}
        hintsRemaining={hintsRemaining}
        completedNumbers={completedNumbers}
        disabled={isPaused || gameState.is_completed || gameState.is_failed}
      />

      {/* Pause Modal */}
      <Modal
        visible={showPauseModal}
        transparent
        animationType="fade"
        onRequestClose={handleResume}
      >
        <View style={styles.pauseModalBackdrop}>
          <View
            style={[styles.pauseModalContainer, { backgroundColor: colors.surface }]}
          >
            <Text style={[styles.pauseTitle, { color: colors.text }]}>
              Gepauzeerd
            </Text>
            <Text style={[styles.pauseSubtitle, { color: colors.textSecondary }]}>
              Neem even pauze, je voortgang is opgeslagen
            </Text>

            <View style={styles.pauseButtons}>
              <Pressable
                onPress={handleResume}
                accessibilityRole="button"
                accessibilityLabel="Doorgaan"
                style={[
                  styles.pauseButton,
                  styles.pauseButtonResume,
                ]}
              >
                <Text style={styles.pauseButtonResumeText}>
                  Doorgaan
                </Text>
              </Pressable>

              <Pressable
                onPress={handleExitGame}
                accessibilityRole="button"
                accessibilityLabel="Afsluiten"
                style={[
                  styles.pauseButton,
                  { backgroundColor: isDark ? "#374151" : "#E5E7EB" },
                ]}
              >
                <Text style={[styles.pauseButtonExitText, { color: colors.text }]}>
                  Afsluiten
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Result Overlay */}
      {result && (
        <SudokuResultOverlay
          result={result}
          visible={true}
          onPlayAgain={handlePlayAgain}
          onBackToMenu={handleBackToMenu}
          winLottieKey={lottieConfig.win_lottie}
          loseLottieKey={lottieConfig.lose_lottie}
          perfectLottieKey={lottieConfig.perfect_lottie}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
  },
  loadingText: {
    fontSize: 16,
  },
  boardContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  pauseModalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  pauseModalContainer: {
    width: "100%",
    maxWidth: 320,
    borderRadius: 24,
    padding: 24,
    alignItems: "center",
  },
  pauseTitle: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 8,
  },
  pauseSubtitle: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 24,
  },
  pauseButtons: {
    width: "100%",
    gap: 12,
  },
  pauseButton: {
    width: "100%",
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  pauseButtonResume: {
    backgroundColor: "#3B82F6",
  },
  pauseButtonResumeText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  pauseButtonExitText: {
    fontSize: 16,
    fontWeight: "600",
  },
});

